import { envConfig } from "@configs/env";
import { STATSIG_AVAILABLE_EVENT, STATSIG_READY_EVENT } from "@configs/statsig";
import { StatsigProvider, StatsigUser, useClientAsyncInit } from "@statsig/react-bindings";
import { StatsigSessionReplayPlugin } from "@statsig/session-replay";
import { StatsigAutoCapturePlugin } from "@statsig/web-analytics";
import { AuthSliceState } from "@stores/auth/slices";
import { RootState } from "@stores/index";
import { PreferenceSliceState } from "@stores/preference/slices";
import logger from "@utils/logger";
import { ReactNode, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { buildUserProperties, mapBrandGroup } from "./utils";

interface AppStatsigProviderProps {
  children: ReactNode;
}

export function AppStatsigProvider({ children }: AppStatsigProviderProps) {
  const clientKey = import.meta.env.VITE_STATSIG_CLIENT_KEY;
  const { self } = useSelector<RootState, AuthSliceState>((state) => state.auth);
  const { serverInfo } = useSelector<RootState, PreferenceSliceState>((state) => state.preferences);

  const hasSetupBeforeUnload = useRef(false);

  // Build comprehensive user object for Statsig
  const statsigUser: StatsigUser = useMemo(
    () =>
      self?.member && serverInfo?.prefix
        ? buildUserProperties(self?.member, serverInfo?.prefix || "")
        : {
            userID: undefined,
            custom: {
              group: mapBrandGroup(envConfig?.brand?.group),
              accountId: envConfig?.accountId,
              brand: envConfig?.brand?.key,
              appVersion: import.meta.env.VITE_APP_VERSION,
            },
            customIDs: {
              group: mapBrandGroup(envConfig?.brand?.group),
              accountId: envConfig?.accountId,
              brand: envConfig?.brand?.key,
            },
          },
    [self?.member, serverInfo?.prefix],
  );

  const { client, isLoading } = useClientAsyncInit(clientKey, statsigUser, {
    plugins: [new StatsigAutoCapturePlugin(), new StatsigSessionReplayPlugin()],
  });

  // Expose client globally for middleware access
  useEffect(() => {
    if (client) {
      (window as any).__statsig_client__ = client;
      try {
        logger._console.log("[Statsig] dispatch", STATSIG_AVAILABLE_EVENT);
        window.dispatchEvent(new CustomEvent(STATSIG_AVAILABLE_EVENT, { detail: { client } }));
        if (client.loadingStatus === "Ready") {
          logger._console.log("[Statsig] dispatch", STATSIG_READY_EVENT);
          window.dispatchEvent(new CustomEvent(STATSIG_READY_EVENT, { detail: { client } }));
        }
      } catch {}
    }
    return () => {
      // Cleanup on unmount
      (window as any).__statsig_client__ = null;
    };
  }, [client]);

  // Emit ready event once the client finishes loading
  useEffect(() => {
    if (!client || isLoading) return;
    try {
      logger._console.log("[Statsig] dispatch (on isLoading=false)", STATSIG_READY_EVENT);
      window.dispatchEvent(new CustomEvent(STATSIG_READY_EVENT, { detail: { client } }));
    } catch {}
  }, [client, isLoading]);

  // Setup application-level beforeunload handling for reliability
  useEffect(() => {
    if (!client || hasSetupBeforeUnload.current) return;

    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      client.shutdown();
      logger._console.log("Statsig gracefully shutdown on page unload");
    };

    // Use beforeunload for graceful shutdown
    window.addEventListener("beforeunload", handleBeforeUnload);

    hasSetupBeforeUnload.current = true;

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [client]);

  if (!clientKey) {
    logger._console.warn("Statsig client key missing - running without analytics");
    return <>{children}</>;
  }

  return (
    <StatsigProvider
      client={client}
      options={{
        environment: { tier: envConfig.deploymentEnv },
      }}
    >
      {children}
    </StatsigProvider>
  );
}
